// frontend/src/pages/LeadsPage.js
// v2.0 - Added search, status filter, and edit modal with full CRUD

import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import apiService, { razobratSpisokObrashcheniy } from '../services/apiService';
import LoadingSpinner from '../components/LoadingSpinner';
import './LeadsPage.css';
import TeamAssignmentModal from '../components/Leads/TeamAssignmentModal';
import FitenkoObrashcheniya from './FitenkoObrashcheniya';
import { useBranding } from '../context/BrandingContext';
import { useUiLanguage } from '../context/UiLanguageContext';
import { formatLeadName } from '../utils/historyFormatters';
import { Card, PageHeader, Button } from '../components/ui';
import { displayEmail, sourceMeta } from '../config/istochnikiObrashcheniy';

const STATUSES = ['ALL', 'NEW', 'CONTACTED', 'QUALIFIED', 'UNQUALIFIED', 'CONVERTED'];

// 🔴 `displayEmail` и `sourceMeta` УЕХАЛИ в `config/istochnikiObrashcheniy.js`
//    (quick-260911-27v): потребителей у них стало ДВА — эта страница и экран
//    обращений fitenko. Копия в двух экранах была бы двумя держателями одного
//    ответа, а ввоз из нового экрана в старый замкнул бы кольцо (старый УЖЕ
//    ввозит новый). Тела функций перенесены ДОСЛОВНО, буква в букву.

export const formatLeadSource = (source, t) => {
    const meta = sourceMeta(source);
    const v = (source || '').toLowerCase();
    if (v.includes('voice') || v.includes('phone')) {
        return { ...meta, label: t('practice.source.phone_sofia', meta.label), isCustom: false };
    }
    if (v.includes('chat')) {
        return { ...meta, label: t('practice.source.webchat_sofia', meta.label), isCustom: false };
    }
    if (v.includes('web') || v.includes('site')) {
        return { ...meta, label: t('practice.source.website', meta.label), isCustom: false };
    }
    return { ...meta, isCustom: true };
};

const formatDate = (dateString, lang) => {
    if (!dateString) return 'N/A';
    try {
        const locale = lang === 'ru' ? 'ru-RU' : 'en-US';
        return new Date(dateString).toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
    } catch { return dateString; }
};

// --- Preview Modal ---
const LeadPreviewModal = ({ lead, onClose }) => {
    const { t, lang } = useUiLanguage();
    if (!lead) return null;
    const src = formatLeadSource(lead.source, t);
    return (
        <div className="lead-modal-backdrop" onClick={onClose}>
            <div className="lead-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="lead-modal-header">
                    <h2>{t('practice.leads.modal_preview_title')}</h2>
                    <button onClick={onClose} className="lead-modal-close-btn">&times;</button>
                </div>
                <div className="lead-modal-body">
                    <p><strong>{t('practice.leads.modal_preview_fullname')}</strong> <span data-user-content="true">{formatLeadName(lead.full_name, t, lang)}</span></p>
                    <p><strong>{t('practice.leads.modal_preview_email')}</strong> <span data-user-content="true">{displayEmail(lead.email)}</span></p>
                    <p><strong>{t('practice.leads.modal_preview_phone')}</strong> <span data-user-content="true">{lead.phone || t('practice.leads.modal_preview_not_provided')}</span></p>
                    <p><strong>{t('practice.leads.modal_preview_source')}</strong> <span data-user-content={src.isCustom ? "true" : undefined}>{src.label}</span></p>
                    <p><strong>{t('practice.leads.modal_preview_status')}</strong> <span className={`status-badge status-${lead.status.toLowerCase()}`}>{t(`practice.leads.filter_${lead.status.toLowerCase()}`) || lead.status}</span></p>
                    <p>
                        <strong>{t('practice.leads.modal_preview_created')}</strong> {formatDate(lead.created_at, lang)}
                        {lead.has_new_activity && (
                            <span className="badge-new-activity" style={{ marginLeft: '8px', fontSize: '0.75rem', padding: '2px 8px', borderRadius: '4px', backgroundColor: 'var(--accent-soft)', color: 'var(--accent-ink)', border: '1px solid var(--accent-border)', fontWeight: 600 }}>
                                {t('practice.leads.badge_new_activity')}
                            </span>
                        )}
                    </p>
                    <hr />
                    <p><strong>{t('practice.leads.modal_preview_notes')}</strong></p>
                    <p data-user-content="true">{lead.notes || '—'}</p>
                    {lead.appointment_datetime && (
                        <div className="consultation-details-block" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
                            <h4 style={{ fontWeight: '600', marginBottom: '0.5rem' }}>{t('practice.leads.modal_preview_scheduled_consultation')}</h4>
                            <p><strong>{t('practice.leads.modal_preview_datetime')}</strong> {formatDate(lead.appointment_datetime, lang)}</p>
                            {lead.appointment_gcal_link && (
                                <p>
                                    <strong>{t('practice.leads.modal_preview_calendar_link')}</strong>{' '}
                                    <a href={lead.appointment_gcal_link} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--info)', textDecoration: 'underline' }}>
                                        {t('practice.leads.modal_preview_view_gcal')}
                                    </a>
                                </p>
                            )}
                        </div>
                    )}
                </div>
                <div className="lead-modal-footer">
                    <Button variant="outline" onClick={onClose}>{t('practice.leads.modal_preview_close')}</Button>
                </div>
            </div>
        </div>
    );
};

// --- Edit Modal ---
export const LeadEditModal = ({ lead, onClose, onSave }) => {
    const { t } = useUiLanguage();
    const [form, setForm] = useState({
        full_name: lead.full_name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        source: lead.source || '',
        status: lead.status || 'NEW',
        notes: lead.notes || '',
    });
    const [saving, setSaving] = useState(false);

    const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const updated = await apiService.updateLead(lead.id, form);
            onSave(updated);
            toast.success(t('practice.leads.modal_edit_success'));
            onClose();
        } catch (err) {
            toast.error(t('practice.leads.modal_edit_failed', { error: err.message }));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="lead-modal-backdrop" onClick={onClose}>
            <div className="lead-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="lead-modal-header">
                    <h2>{t('practice.leads.modal_edit_title')}</h2>
                    <button onClick={onClose} className="lead-modal-close-btn">&times;</button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="lead-modal-body" style={{ display: 'grid', gap: '0.75rem' }}>
                        <label>
                            <span style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('practice.leads.col_name')}</span>
                            <input className="form-input" name="full_name" value={form.full_name} onChange={handleChange} required data-user-content="true" />
                        </label>
                        <label>
                            <span style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('practice.leads.col_email')}</span>
                            <input className="form-input" name="email" type="email" value={form.email} onChange={handleChange} data-user-content="true" />
                        </label>
                        <label>
                            <span style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('practice.leads.col_phone')}</span>
                            <input className="form-input" name="phone" value={form.phone} onChange={handleChange} data-user-content="true" />
                        </label>
                        <label>
                            <span style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('practice.leads.col_source')}</span>
                            <input className="form-input" name="source" value={form.source} onChange={handleChange} />
                        </label>
                        <label>
                            <span style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('practice.leads.col_status')}</span>
                            <select className="form-input" name="status" value={form.status} onChange={handleChange}>
                                {['NEW', 'CONTACTED', 'QUALIFIED', 'UNQUALIFIED', 'CONVERTED'].map(s => (
                                    <option key={s} value={s}>{t(`practice.leads.filter_${s.toLowerCase()}`) || s}</option>
                                ))}
                            </select>
                        </label>
                        <label>
                            <span style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>{t('practice.leads.modal_preview_notes')}</span>
                            <textarea className="form-input" name="notes" value={form.notes} onChange={handleChange} rows={4} data-user-content="true" />
                        </label>
                    </div>
                    <div className="lead-modal-footer">
                        <Button type="button" onClick={onClose} variant="outline" disabled={saving}>{t('practice.leads.modal_edit_cancel')}</Button>
                        <Button type="submit" variant="primary" disabled={saving}>
                            {saving ? t('practice.leads.modal_edit_saving') : t('practice.leads.modal_edit_save')}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Main Page ---
// 🔴 РУБЕЖ АРЕНДАТОРА СТОИТ В РАЗМЕТКЕ, А МАРШРУТ ОСТАЁТСЯ ОБЩИМ (Д-10).
//    `/leads` объявлен в `App.js:117` без гейта, и пункт `leads` виден всем
//    четверым арендаторам. Образец рядом: `HistoryPage` гейтит СОДЕРЖИМОЕ,
//    оставляя маршрут общим. У mlg, eln и elfny ниже идёт РОВНО ТА ЖЕ разметка,
//    что была: ни одного изменённого узла, кроме чтения новой формы ответа.
const LeadsPage = () => {
    const { isFitenko } = useBranding();
    const { t, lang } = useUiLanguage();
    const [leads, setLeads] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedLead, setSelectedLead] = useState(null);
    const [editingLead, setEditingLead] = useState(null);
    const [leadToAssign, setLeadToAssign] = useState(null);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [activeMenuId, setActiveMenuId] = useState(null);
    // ТРЕТЬЕ СОСТОЯНИЕ: ответ не той формы — это ОТКАЗ ИЗМЕРИТЕЛЯ, и он говорит
    // словами. Пустая таблица сказала бы «обращений нет» — это была бы неправда.
    const [otkazOtveta, setOtkazOtveta] = useState(null);

    useEffect(() => {
        if (!activeMenuId) return;
        const handleOutsideClick = () => setActiveMenuId(null);
        document.addEventListener('click', handleOutsideClick);
        return () => document.removeEventListener('click', handleOutsideClick);
    }, [activeMenuId]);

    useEffect(() => {
        const fetchLeads = async () => {
            try {
                // 🔴 Доводы НЕ ПЕРЕДАЮТСЯ: умолчание ручки (skip=0, limit=100) —
                //    ровно то, что эта страница получала до смены формы ответа.
                //    Прежняя разметка mlg/eln/elfny обязана показать ТО ЖЕ.
                const razbor = razobratSpisokObrashcheniy(await apiService.getLeads());
                if (razbor.rod === 'otkaz') {
                    setOtkazOtveta(razbor.tekst);
                    setLeads([]);
                    return;
                }
                setOtkazOtveta(null);
                setLeads(razbor.items);
            } catch (error) {
                toast.error(`Failed to fetch leads: ${error.message}`);
            } finally {
                setIsLoading(false);
            }
        };
        fetchLeads();
    }, []);

    const filteredLeads = useMemo(() => {
        const q = search.toLowerCase();
        return leads.filter(lead => {
            const matchesSearch = !q ||
                lead.full_name?.toLowerCase().includes(q) ||
                lead.email?.toLowerCase().includes(q) ||
                lead.phone?.toLowerCase().includes(q);
            const matchesStatus = statusFilter === 'ALL' || lead.status === statusFilter;
            return matchesSearch && matchesStatus;
        });
    }, [leads, search, statusFilter]);

    const handleConvert = async (leadId) => {
        try {
            const response = await apiService.convertLead(leadId);
            toast.success(response.message || t('practice.leads.convert_success'));
            setLeads(prev => prev.map(l =>
                l.id === leadId ? { ...l, status: 'CONVERTED', case_id: response.case_id } : l
            ));
        } catch (error) {
            toast.error(t('practice.leads.convert_failed', { error: error.message }));
        }
    };

    const handleDelete = async (leadId, leadName) => {
        const displayName = formatLeadName(leadName, t, lang);
        if (!window.confirm(t('practice.leads.delete_confirm', { name: displayName }))) return;
        try {
            await apiService.deleteLead(leadId);
            toast.success(t('practice.leads.delete_success', { name: displayName }));
            setLeads(prev => prev.filter(l => l.id !== leadId));
        } catch (error) {
            toast.error(t('practice.leads.delete_failed', { error: error.message }));
        }
    };

    const handleSaveEdit = (updatedLead) => {
        setLeads(prev => prev.map(l => l.id === updatedLead.id ? updatedLead : l));
    };

    const handleAssignTeam = (lead) => {
        if (lead.status.toLowerCase() === 'converted' && lead.case_id) {
            setLeadToAssign(lead);
        } else {
            toast.error(t('practice.leads.case_id_not_available'));
        }
    };

    // Экран арендатора fitenko живёт СВОИМ файлом. Это сразу и рубеж, и граница
    // измерения цены рисунка — тот же довод, по которому в задаче u2q карточка
    // события выехала в свой файл.
    if (isFitenko) return <FitenkoObrashcheniya />;

    if (isLoading) return <LoadingSpinner message={t('practice.leads.loading')} />;

    return (
        <>
            {selectedLead && <LeadPreviewModal lead={selectedLead} onClose={() => setSelectedLead(null)} />}
            {editingLead && (
                <LeadEditModal
                    lead={editingLead}
                    onClose={() => setEditingLead(null)}
                    onSave={handleSaveEdit}
                />
            )}
            {leadToAssign && (
                <TeamAssignmentModal
                    caseId={leadToAssign.case_id}
                    caseName={formatLeadName(leadToAssign.full_name, t, lang)}
                    onClose={() => setLeadToAssign(null)}
                    onAssignmentSuccess={() => setLeadToAssign(null)}
                />
            )}

            <div className="leads-page-container container">
                <PageHeader
                    title={t('practice.leads.title')}
                    subtitle={t('practice.leads.subtitle')}
                />

                {/* Search & Filter Bar */}
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                    <input
                        className="form-input"
                        style={{ flex: '1', minWidth: '200px', maxWidth: '380px' }}
                        type="text"
                        placeholder={t('practice.leads.search_placeholder')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <select
                        className="form-input"
                        style={{ minWidth: '160px' }}
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        {STATUSES.map(s => (
                            <option key={s} value={s}>{s === 'ALL' ? t('practice.leads.filter_all') : (t(`practice.leads.filter_${s.toLowerCase()}`) || s)}</option>
                        ))}
                    </select>
                    <span style={{ display: 'flex', alignItems: 'center', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                        {t('practice.leads.count_summary', { filtered: filteredLeads.length, total: leads.length })}
                    </span>
                </div>

                <Card className="results-card">
                  <div className="table-scroll">
                    <table className="cases-table">
                        <thead>
                            <tr>
                                <th className="col-name">{t('practice.leads.col_name')}</th>
                                <th className="col-email">{t('practice.leads.col_email')}</th>
                                <th className="col-phone">{t('practice.leads.col_phone')}</th>
                                <th className="col-source">{t('practice.leads.col_source')}</th>
                                <th className="col-status">{t('practice.leads.col_status')}</th>
                                <th className="col-date">{t('practice.leads.col_date')}</th>
                                <th className="col-actions">{t('practice.leads.col_actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLeads.length > 0 ? (
                                filteredLeads.map(lead => (
                                    <tr key={lead.id}>
                                        <td className="col-name" data-label={t('practice.leads.col_name')} data-user-content="true">
                                            {formatLeadName(lead.full_name, t, lang)}
                                            {lead.has_new_activity && (
                                                <span className="badge-new-activity" style={{ marginLeft: '8px', fontSize: '0.75rem', padding: '2px 6px', borderRadius: '4px', backgroundColor: 'var(--accent-soft)', color: 'var(--accent-ink)', border: '1px solid var(--accent-border)', fontWeight: 600, display: 'inline-block' }}>
                                                    {t('practice.leads.badge_new_activity')}
                                                </span>
                                            )}
                                        </td>
                                        <td className="col-email" data-label={t('practice.leads.col_email')} title={displayEmail(lead.email)} data-user-content="true">{displayEmail(lead.email)}</td>
                                        <td className="col-phone" data-label={t('practice.leads.col_phone')} data-user-content="true">{lead.phone || '—'}</td>
                                        <td className="col-source" data-label={t('practice.leads.col_source')}>
                                            {(() => {
                                                const src = formatLeadSource(lead.source, t);
                                                return (
                                                    <span className={`source-badge ${src.cls}`} data-user-content={src.isCustom ? "true" : undefined}>
                                                        {src.icon} {src.label}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                        <td className="col-status" data-label={t('practice.leads.col_status')}>
                                            <span className={`status-badge status-${lead.status.toLowerCase()}`}>{t(`practice.leads.filter_${lead.status.toLowerCase()}`) || lead.status}</span>
                                        </td>
                                        <td className="col-date" data-label={t('practice.leads.col_date')}>{new Date(lead.created_at).toLocaleDateString(lang === 'ru' ? 'ru-RU' : 'en-US')}</td>
                                        <td className={`actions-cell ${activeMenuId === lead.id ? 'menu-open' : ''}`} data-label={t('practice.leads.col_actions')}>
                                            <div className="actions-inner">
                                                <Button variant="outline" size="sm" onClick={() => setSelectedLead(lead)}>
                                                    {t('practice.leads.btn_view')}
                                                </Button>
                                                <div className="actions-dropdown-wrap">
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        className="actions-menu-trigger"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setActiveMenuId(activeMenuId === lead.id ? null : lead.id);
                                                        }}
                                                        title={t('practice.leads.btn_more_actions')}
                                                        aria-label={t('practice.leads.btn_more_actions')}
                                                    >
                                                        ⋯
                                                    </Button>
                                                    {activeMenuId === lead.id && (
                                                        <div className="actions-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                                                            <button
                                                                type="button"
                                                                className="actions-dropdown-item"
                                                                onClick={() => {
                                                                    setActiveMenuId(null);
                                                                    setEditingLead(lead);
                                                                }}
                                                            >
                                                                {t('practice.leads.btn_edit')}
                                                            </button>
                                                            {lead.status.toLowerCase() !== 'converted' ? (
                                                                <button
                                                                    type="button"
                                                                    className="actions-dropdown-item"
                                                                    onClick={() => {
                                                                        setActiveMenuId(null);
                                                                        handleConvert(lead.id);
                                                                    }}
                                                                >
                                                                    {t('practice.leads.btn_convert')}
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    className="actions-dropdown-item"
                                                                    disabled={!lead.case_id}
                                                                    title={!lead.case_id ? t('practice.leads.case_id_not_available') : t('practice.leads.btn_assign_team')}
                                                                    onClick={() => {
                                                                        if (lead.case_id) {
                                                                            setActiveMenuId(null);
                                                                            handleAssignTeam(lead);
                                                                        }
                                                                    }}
                                                                >
                                                                    {t('practice.leads.btn_assign_team')}
                                                                </button>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="actions-dropdown-item item-danger"
                                                                onClick={() => {
                                                                    setActiveMenuId(null);
                                                                    handleDelete(lead.id, lead.full_name);
                                                                }}
                                                            >
                                                                {t('practice.leads.btn_delete')}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" className="no-results">
                                        {otkazOtveta
                                            ? `Не удалось прочитать ответ сервера: ${otkazOtveta}. Это отказ измерителя, а не «обращений нет».`
                                            : (search || statusFilter !== 'ALL' ? (lang === 'ru' ? 'Нет обращений по заданным фильтрам.' : 'No leads match your filters.') : t('practice.leads.no_leads'))}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                  </div>
                </Card>
            </div>
        </>
    );
};

export default LeadsPage;
